<template>
  <div class="global-accounts">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-s-custom"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.globalAccounts') }}</h2>
          <p class="section-header__subtitle">{{ pagination.total }} {{ $t('settings.entries') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          icon="el-icon-back"
          @click="$router.push({path: '/app/global'})">
          {{ $t('settings.backToDashboard') }}
        </base-button>
        <base-button
          type="primary"
          icon="el-icon-plus"
          @click="$router.push({path: '/app/global/accounts/create'})">
          {{ $t('settings.createAccount') }}
        </base-button>
        <base-button
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadAccounts">
          {{ $t('settings.refresh') }}
        </base-button>
      </div>
    </div>

    <div class="filter-bar">
      <div class="search-container">
        <el-input
          class="table-search"
          v-model="searchTerm"
          :placeholder="$t('settings.account.search')"
          prefix-icon="el-icon-search"
          clearable
          @clear="clearSearch"
          @input="debouncedSearch">
        </el-input>
        <div class="search-hint">{{ $t('settings.searchAccountHint') }}</div>
      </div>

      <el-select
        class="type-filter"
        v-model="accountTypeFilter"
        :placeholder="$t('settings.account.typeFilter')"
        clearable
        @change="onAccountTypeFilterChange">
        <el-option :label="$t('settings.account.localAccounts')" :value="true"></el-option>
        <el-option :label="$t('settings.account.ldapAccounts')" :value="false"></el-option>
      </el-select>
    </div>

    <div class="table-card">
      <base-table
        :data="accounts"
        :loading="loading"
        :empty-text="$t('settings.accountsTableEmpty')"
        :row-class-name="getRowClassName"
        class="connections-table"
        style="width: 100%">

        <el-table-column prop="appLoginName" min-width="170" :label="$t('base.appLoginName')">
          <template slot-scope="scope">
            <span class="cell-bold">{{ scope.row.appLoginName }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="firstName" min-width="130" :label="$t('base.firstName')">
          <template slot-scope="scope">
            <span v-if="scope.row.firstName">{{ scope.row.firstName }}</span>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="lastName" min-width="130" :label="$t('base.lastName')">
          <template slot-scope="scope">
            <span v-if="scope.row.lastName">{{ scope.row.lastName }}</span>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="email" min-width="200" :label="$t('base.email')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.email">
              <i class="el-icon-message cell-url__icon"></i>
              <span>{{ scope.row.email }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center" :label="$t('settings.account.type')">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.isLocalAccount" type="success" size="mini" class="domain-tag">
              {{ $t('settings.account.local') }}
            </el-tag>
            <el-tag v-else type="info" size="mini" class="domain-tag">
              {{ $t('settings.account.ldap') }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column min-width="160" :label="$t('settings.customer.name')">
          <template slot-scope="scope">
            <div class="tag-list" v-if="scope.row.customers && scope.row.customers.length > 0">
              <el-tag
                v-for="customer in scope.row.customers"
                :key="customer.id"
                type="info"
                size="mini"
                class="domain-tag">
                {{ customer.name }}
              </el-tag>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="150" :label="$t('settings.tenant.name')">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.tenant && scope.row.tenant.name" type="info" size="mini" class="domain-tag">
              {{ scope.row.tenant.name }}
            </el-tag>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column width="130" align="center" :label="$t('settings.authority')">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.userAuthority === 'SUPER_ADMIN'" type="danger" size="mini" class="domain-tag">
              Super Admin
            </el-tag>
            <el-tag v-if="scope.row.userAuthority === 'ADMIN'" size="mini" class="domain-tag">
              Admin
            </el-tag>
            <el-tag v-if="scope.row.userAuthority === 'REPORTER'" type="info" size="mini" class="domain-tag">
              Reporter
            </el-tag>
            <el-tag v-if="scope.row.userAuthority === 'USER'" type="info" size="mini" class="domain-tag">
              User
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center" :label="$t('settings.active')">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.active"
              :disabled="isCurrentUser(scope.row)"
              @click.native.stop
              @change="toggleAccountActive(scope.row)">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column align="center" fixed="right" width="180" :label="$t('settings.operations')">
          <template slot-scope="account">
            <el-tag v-if="isCurrentUser(account.row)" type="success" size="mini" class="domain-tag">
              {{ $t('settings.account.currentUser') }}
            </el-tag>
            <div class="action-cell" v-else>
              <el-tooltip :content="$t('settings.edit')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  @click.stop.prevent="editAccount(account.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.account.sendEmail')" placement="top">
                <el-button
                  :disabled="!account.row.email"
                  type="primary"
                  icon="el-icon-message"
                  size="mini"
                  circle
                  @click.stop.prevent="sendTestEmail(account.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.account.sendPush')" placement="top">
                <el-button
                  :disabled="!account.row.hasPushToken"
                  type="primary"
                  icon="el-icon-mobile-phone"
                  size="mini"
                  circle
                  @click.stop.prevent="sendTestPush(account.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.account.delete')" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  @click.stop.prevent="showDeleteAccountDialog(account.row)">
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </base-table>

      <el-pagination
        v-if="pagination.total > 0"
        class="accounts-pagination"
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange"
        :current-page.sync="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>

    <base-delete-dialog
      :visible.sync="deleteAccountDialog"
      :title="$t('settings.deleteAccount')"
      :text="$t('settings.deleteAccountInfoText')"
      :confirm-label="$t('settings.deleteAccount')"
      :loading="loading"
      @confirm="confirmDeleteAccount">
    </base-delete-dialog>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {adminAccountService} from '@/services/AdminAccountService.class';
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';

export default {
  name: "GlobalAccounts",
  components: {BaseTable, BaseDeleteDialog, BaseButton},
  data() {
    return {
      accounts: [],
      searchTerm: '',
      accountTypeFilter: null,
      loading: false,
      deleteAccountDialog: false,
      selectedAccount: null,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      searchTimeout: null,
      currentUserId: null
    };
  },
  mounted() {
    this.loadAccounts();
    this.getCurrentUserId();
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    getCurrentUserId() {
      const currentUser = this.$store.getters['auth/getUser'];
      if (currentUser && currentUser.id) {
        this.currentUserId = currentUser.id;
      }
    },

    isCurrentUser(account) {
      return account.id === this.currentUserId;
    },

    getRowClassName({row}) {
      return this.isCurrentUser(row) ? 'current-user-row' : '';
    },

    loadAccounts() {
      this.loading = true;

      const filterParams = {
        search: this.searchTerm || undefined,
        localOnly: this.accountTypeFilter
      };

      const pageParams = {
        page: this.pagination.page - 1, // API uses 0-based indexing
        pageSize: this.pagination.pageSize,
        pageSortDirection: 'ASC'
      };

      adminAccountService.getAllAccounts(filterParams, pageParams)
        .then(response => {
          if (response.data && response.data.content) {
            // Paginated response with spring data format
            this.accounts = response.data.content;
            this.pagination.total = response.data.totalElements;
          } else if (response.data && response.data.totalElements !== undefined) {
            // Another paginated format
            this.accounts = response.data;
            this.pagination.total = response.data.totalElements;
          } else if (Array.isArray(response.data)) {
            // Non-paginated array response
            this.accounts = response.data;
            this.pagination.total = response.data.length;
          } else {
            this.accounts = [];
            this.pagination.total = 0;
          }
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadAccounts();
    },
    handlePageChange() {
      this.loadAccounts();
    },
    clearSearch() {
      this.searchTerm = '';
      this.pagination.page = 1;
      this.loadAccounts();
    },
    onAccountTypeFilterChange() {
      this.pagination.page = 1;
      this.loadAccounts();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadAccounts();
      }, 300);
    },
    toggleAccountActive(account) {
      adminAccountService.setAccountActive(account.id, account.active)
        .then(() => {
          this.$message.success(
            account.active
              ? this.$t('settings.account.activated')
              : this.$t('settings.account.deactivated')
          );
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
          account.active = !account.active;
        });
    },
    editAccount(account) {
      this.$router.push({path: `/app/global/accounts/${account.id}/edit`});
    },
    sendTestEmail(account) {
      this.loading = true;
      adminAccountService.sendTestEmail(account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testEmailSent'));
        })
        .catch(() => {
          this.$message.error(this.$t('settings.testEmailFailed'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    sendTestPush(account) {
      this.loading = true;
      adminAccountService.sendTestPush(account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testPushSent'));
        })
        .catch(() => {
          this.$message.error(this.$t('settings.testPushFailed'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showDeleteAccountDialog(account) {
      this.selectedAccount = account;
      this.deleteAccountDialog = true;
    },
    confirmDeleteAccount() {
      if (!this.selectedAccount) {
        return;
      }
      this.loading = true;
      adminAccountService.deleteAccount(this.selectedAccount.id)
        .then(() => {
          this.$message.success(this.$t('settings.accountDeleted'));
          this.deleteAccountDialog = false;
          this.selectedAccount = null;
          this.loadAccounts();
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
}
</script>

<style lang="scss">
.global-accounts .connections-table .el-table__body-wrapper tr.current-user-row td {
  background: #f4faf0 !important;
}
</style>

<style scoped lang="scss">
.global-accounts {
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;

  .search-container {
    flex: 1;
    max-width: 400px;

    .table-search {
      width: 100%;
      margin-bottom: 5px;
    }

    .search-hint {
      font-size: 12px;
      color: var(--text-secondary);
      margin-left: 5px;
    }
  }

  .type-filter {
    width: 220px;
  }
}

.table-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.accounts-pagination {
  padding: 14px 0;
  text-align: center;
  border-top: 1px solid var(--border);
}

.cell-bold {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-primary);
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;

  &__icon {
    color: var(--text-muted);
    font-size: 14px;
    flex-shrink: 0;
  }
}

.cell-empty {
  color: var(--text-muted);
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .filter-bar {
    flex-direction: column;

    .search-container,
    .type-filter {
      max-width: none;
      width: 100%;
    }
  }
}
</style>
